import React from 'react';
import styled from "styled-components";
import useScreenSize from "../../hooks/useScreenSize";
import useSectionState from "../../hooks/useSectionState";

import { Form } from "../form/FormComponents";
import CardViewFormSection from "../cardView/CardViewFormSection";
import PdfPager from "../pdf/PdfPager";
import { useStore } from "../pageView/Page";
import ModalSection from "../modal/ModalSection";
import DocumentPreview from "../documents/DocumentPreview";
import DocumentNavigator from "../documents/DocumentNavigator";
import LineDocumentsPanel, { LineDocumentsCount } from "../documents/LineDocumentsPanel";
import useTranslation from "../../hooks/useTranslation";
import { Icon_Arrow_Left, Icon_Arrow_Right } from "../../styles/icons";
import RichText from "../richText/RichText";
import RichTextDisplay from "../richText/RichTextDisplay";

const SidePanel = styled.div`
  background: ${({ theme }) => theme.page.containerBg};
  //width: ${(props) => (props.width ? props.width : "400px")};
  width: 100%;
  padding: 1rem;
  border-radius: 1rem;
`;

/*
 * The space around the grey panel, when the panel's config declares it
 * (`padding`, CSS shorthand). The multi view pulls its columns flush to the
 * page edges, and the main view puts its own gap back; a side panel says what
 * it needs rather than getting one by where it sits.
 */
const PanelSpacing = styled.div`
  padding: ${({ $padding }) => $padding};
`;

/*
 * A collapsible panel (ResizableView) folded to a strip: the toggle on top,
 * then each section's counter — a line's documents. The whole strip opens it.
 * Folded, the sections themselves are not drawn at all.
 */
const Strip = styled.div`
  height: 100%;
  min-height: 12rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-radius: 1rem;
  background: ${({ theme }) => theme.page.containerBg};
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.table.hover}; }
`

/*
 * A round button, so it reads as one before it is pointed at: a ring at rest,
 * filled with the accent on hover. On the folded strip the whole strip opens
 * the panel, so pointing anywhere on it lights the button up too.
 */
const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.form.border};
  background: ${({ theme }) => theme.page.background};
  color: ${({ theme }) => theme.font.label};
  cursor: pointer;
  transition: background-color .15s ease-in-out, color .15s ease-in-out, border-color .15s ease-in-out;

  svg { font-size: 0.875rem; }

  &:hover,
  ${Strip}:hover & {
    background: ${({ theme }) => theme.tab.navLinkColor};
    border-color: ${({ theme }) => theme.tab.navLinkColor};
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.tab.navLinkColor};
    outline-offset: 2px;
  }
`

const PanelBar = styled.div`
  display: flex;
  justify-content: ${({ $position }) => $position === 'left' ? 'flex-end' : 'flex-start'};
  margin: -0.5rem -0.5rem 0.25rem;
`

const SideForm = ({ config, options, formType, collapsed = false, onToggleCollapsed }) => {
  if (!config) return <span>No Config found for {config}</span>
  const [state, setState] = useStore(s => s)
  const { t } = useTranslation()

  const { isSmallTablet } = useScreenSize();

  const { expanded, toggleExpanded } = useSectionState(config, options);

  //   This ensures the Sideform is only shown when the Filter is correct.

  // Arrows point the way the panel moves: a right panel closes rightwards.
  const onRight = config.position !== 'left'
  const closeIcon = onRight ? Icon_Arrow_Right : Icon_Arrow_Left
  const openIcon = onRight ? Icon_Arrow_Left : Icon_Arrow_Right

  if (collapsed) {
    return (
      <PanelSpacing $padding={config.padding ?? 0} style={{ height: '100%' }}>
        <Strip role="button" title={t('all.panel.expand')} onClick={onToggleCollapsed}>
          <ToggleButton type="button" aria-label={t('all.panel.expand')}>{openIcon}</ToggleButton>
          {config.sections?.map((section, i) => section.meta.type === 'lineDocuments'
            ? <LineDocumentsCount key={i} meta={section.meta} />
            : null)}
        </Strip>
      </PanelSpacing>
    )
  }

  return (
    <PanelSpacing $padding={config.padding ?? 0}>
    <SidePanel name="SidePanel" width={config.width}>
      {onToggleCollapsed && (
        <PanelBar $position={config.position}>
          <ToggleButton type="button" title={t('all.panel.collapse')} aria-label={t('all.panel.collapse')}
            onClick={onToggleCollapsed}>
            {closeIcon}
          </ToggleButton>
        </PanelBar>
      )}
      <Form>
        <Form.Shell name="Form-Shell" width={config.width}>
          {config.sections &&
            config.sections.map((section, i) => {
              return (
                <React.Fragment key={i}>
                  {section.meta.type === "form" ? (
                    <CardViewFormSection
                      expanded={expanded[section.id]}
                      toggleExpanded={toggleExpanded}
                      section={section}
                      key={i}
                      entity={config.entity}
                      //      dispatch={dispatch}
                      //      validated={validated}
                      useStore={useStore}
                    />
                  ) : section.meta.type === "pdf" ? (
                    <PdfPager entity={config.entity} state={state} />
                  ) : section.meta.type === "documentPreview" ? (
                    <DocumentPreview />
                  ) : section.meta.type === "documentNavigator" ? (
                    <DocumentNavigator meta={section.meta} entity={config.entity} />
                  ) : section.meta.type === "lineDocuments" ? (
                    <LineDocumentsPanel meta={section.meta} entity={config.entity} />
                  ) : section.meta.type === "richtextDisplay" ? (
                    <RichTextDisplay value={state?.content} />
                  ) : section.meta.type === "richtextEditor" ? (
                    <RichText
                      name={section.meta.field || 'content'}
                      value={state?.[section.meta.field || 'content'] || ''}
                      setValue={(msg) => {
                        if (msg?.target) setState({ [msg.target.name]: msg.target.value })
                      }}
                      resizable={true}
                    />
                  ) : section.meta.type === "modalSection" ? (
                      <ModalSection
                        expanded={expanded[section.id]}
                        toggleExpanded={toggleExpanded}
                        section={section}
                        key={i}
                        entity={config.entity}
                        useStore={useStore}
                        options={options}
                      />) : (
                      <></>
                    )
                  }
                  </React.Fragment>
              );
            })}
        </Form.Shell>
      </Form>
    </SidePanel>
    </PanelSpacing>
  );
};

export default SideForm;
